import React, { useMemo } from 'react';
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { READINESS_LEVELS } from '../../types/gamification';
import { HintsPanel } from './HintsPanel';

interface MobileReadinessWidgetProps {
  totalXP: number;
  readinessPercentage: number;
  completedTasks: string[];
  nextTask?: {
    id: string;
    title: string;
    description: string;
    xpReward: number;
  } | null;
  isExpanded: boolean;
  onToggle: () => void;
}

export const MobileReadinessWidget: React.FC<MobileReadinessWidgetProps> = ({
  totalXP,
  readinessPercentage,
  completedTasks,
  nextTask,
  isExpanded,
  onToggle,
}) => {
  const navigate = useNavigate();
  
  const currentLevel = useMemo(() => 
    READINESS_LEVELS.find(
      (l) => readinessPercentage >= l.min && readinessPercentage <= l.max
    ) || READINESS_LEVELS[0],
  [readinessPercentage]);

  const progress = Math.min(Math.max(readinessPercentage, 0), 100);
  const radius = 18;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (progress / 100) * circumference;

  return (
    <div className="bg-white border-b border-neutral-200 shadow-sm">
      {/* Header / Collapsed View */}
      <div 
        className="flex items-center justify-between px-4 py-2 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          {/* Circular Progress */}
          <div className="relative h-10 w-10 flex-shrink-0">
            <svg className="h-10 w-10 rotate-[-90deg]" viewBox="0 0 48 48">
              <circle
                className="text-neutral-100"
                strokeWidth="4"
                stroke="currentColor"
                fill="transparent"
                r={radius}
                cx="24"
                cy="24"
              />
              <circle
                className="text-primary transition-all duration-500 ease-out"
                strokeWidth="4"
                stroke="currentColor"
                fill="transparent"
                r={radius}
                cx="24"
                cy="24"
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[10px] font-bold text-neutral-800">{Math.round(progress)}%</span>
            </div>
          </div>

          <div className="flex flex-col">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-bold text-neutral-900">AI Readiness</span>
              <span className="text-sm">{currentLevel.emoji}</span>
            </div>
            <span className="text-xs text-neutral-500">{currentLevel.label} • {totalXP} XP</span>
          </div>
        </div>

        <button 
          className="p-1 text-neutral-400"
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-0 animate-in slide-in-from-top-2 duration-200">
           <div className="pt-2 border-t border-neutral-100 mt-2">
              <HintsPanel
                userGamification={{
                  totalXP,
                  readinessPercentage,
                  completedTasks,
                }}
                nextTask={nextTask}
                compact={true}
              />
           </div>
           
           <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
              navigate('/ai-readiness');
            }}
            className="w-full mt-3 flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-primary bg-primary/5 rounded-lg hover:bg-primary/10 transition-colors"
          >
            View Full Progress
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};

export default MobileReadinessWidget;
